/**
 * #3365 arkin CATCH 재고 전용 엑셀 포맷.
 *
 * 고객이 매일 쓰는 "AH CATCH STOCK SHEET" FORM 시트(B1:Z53)를 조립한다.
 * 위쪽 = BW(좌) · JERRY CURL(중) · DEEP(우), 아래쪽 = ST(좌) · BOHEMIAN(우).
 * 블록마다 번들 10~30" / 5X5 클로저 10~18" / 13X5 10~18" 행이 있고 색상 열은 NAT·P1B/27·99J·RED·613 이다.
 * 요청 범위는 HWCA 번들과 CC5X5 클로저의 4개 스타일뿐이다. JERRY CURL·13X5 는 양식 라벨과 빈칸만 남긴다.
 * 수량은 TOUCH(#3303)와 같이 available_quantity 기준이고, 금액은 허용된 경우에만 available × cost 다.
 *
 * 이 파일은 프로젝트 모듈에 의존하지 않는 순수 로직만 담는다(단위 테스트 대상).
 * 실제 workbook 쓰기는 화면이 excelComplicated() + XLSX.writeFile() 로 처리한다.
 */
import { excelDateSerial } from '@/utils/touchStockSheet';

export const CATCH_CLASS_GROUP = 'CATCH';
export const CATCH_SHEET_NAME = 'FORM';
export const CATCH_SHEET_TITLE = 'AH CATCH STOCK  SHEET';

const SAMPLE_LIMIT = 5;

// 색상 열. 코드 마스터에는 NATURAL 로 등록되어 있고 고객 양식은 NAT 로 줄여 쓴다.
export const CATCH_COLORS = [
	{ code: 'NATURAL', label: 'NAT' },
	{ code: 'P1B/27', label: 'P1B/27' },
	{ code: '99J', label: '99J' },
	{ code: 'RED', label: 'RED' },
	{ code: '613', label: '613' },
];

function evenLengths(from, to) {
	const lengths = [];
	for (let length = from; length <= to; length += 2) lengths.push(length);
	return lengths;
}

/**
 * 스타일 블록. 셀은 class code(계열 접두 + 스타일 코드 + 길이)로만 정한다.
 * arkin 마스터는 BOHEMIA 클래스(HWCABO*, CC5X5BO*)의 dept_a_code 가 'BW' 라서 부서코드로 나누면 BW 로 섞인다.
 */
export const CATCH_BLOCKS = [
	{ key: 'BW', style_code: 'BW' },
	{ key: 'DEEP', style_code: 'DP' },
	{ key: 'ST', style_code: 'ST' },
	{ key: 'BOHEMIAN', style_code: 'BO' },
];

/**
 * 집계 대상 계열. CATCH 그룹에는 가발 등 다른 계열도 있으므로 이 두 접두로 먼저 거른다.
 * 클로저 마스터에는 20" 가 있지만 고객 양식은 18" 까지라 행을 늘리지 않고 미매핑으로 안내한다(2026-10-07 사용자 결정).
 */
export const CATCH_GROUPS = [
	{ key: 'BUNDLE', class_prefix: 'HWCA', lengths: evenLengths(10, 30) },
	{ key: '5X5', class_prefix: 'CC5X5', lengths: evenLengths(10, 18) },
];

function isNumeric(value) {
	return value !== null && value !== undefined && value !== '' && !isNaN(Number(value));
}

function toNumber(value) {
	return isNumeric(value) ? Number(value) : 0;
}

function round2(value) {
	return Math.round((value + Number.EPSILON) * 100) / 100;
}

function trimmed(value) {
	return String(value === null || value === undefined ? '' : value).trim();
}

function isTargetClass(class_code) {
	return CATCH_GROUPS.some(group => class_code.startsWith(group.class_prefix));
}

/**
 * 평면 raw data 행을 고정 셀에 정확히 한 번씩 배정한다.
 * - HWCA/CC5X5 계열이 아닌 행은 요청 범위 밖이라 excluded 로만 센다(경고 없음).
 * - 대상 계열인데 셀이 없는 행(20" 클로저, 새 색상, 예상 밖 스타일)은 unmapped 로 남긴다.
 * - 같은 item_id 가 다시 오면 합산하지 않고 duplicates 로 분리한다. 공용 SQL 의 SIZ JOIN 회사 조건 오타가
 *   행을 복제할 수 있어서다(ItemMasterMapper.xml selectItemListCommon). 서로 다른 item 이 같은 셀이면 합산한다.
 */
export function buildCatchStockMatrix(rows, options) {
	const include_value = !!(options && options.include_value);

	const color_index = new Map();
	CATCH_COLORS.forEach((color, index) => color_index.set(color.code, index));

	const class_index = new Map();
	CATCH_BLOCKS.forEach((block, block_index) => CATCH_GROUPS.forEach((group, group_index) => group.lengths.forEach((length, row_index) => class_index.set(group.class_prefix + block.style_code + length, { block_index, group_index, row_index }))));

	const blocks = CATCH_BLOCKS.map(block => ({
		key: block.key,
		groups: CATCH_GROUPS.map(group => ({
			key: group.key,
			rows: group.lengths.map(length => ({
				length,
				cells: CATCH_COLORS.map(() => ({ quantity: 0, value: 0 })),
			})),
		})),
		color_quantities: CATCH_COLORS.map(() => 0),
		color_values: CATCH_COLORS.map(() => 0),
		quantity: 0,
		value: 0,
	}));

	const unmapped = { quantity: 0, value: 0, row_count: 0, samples: [] };
	const duplicates = { quantity: 0, row_count: 0, samples: [] };
	const excluded = { quantity: 0, row_count: 0 };
	const zero_value = { rows: 0, quantity: 0 };
	const seen_item_ids = new Set();
	let source_quantity = 0;
	let source_value = 0;

	(rows || []).forEach(row => {
		// 수량 원천은 available_quantity 뿐이다. on_hand 로 대체하지 않는다.
		const quantity = toNumber(row.available_quantity);
		const class_code = trimmed(row.class_code);
		const color_code = trimmed(row.color_code);

		if (!isTargetClass(class_code)) {
			excluded.quantity += quantity;
			excluded.row_count++;
			return;
		}

		const item_id = trimmed(row.item_id);
		if (item_id) {
			if (seen_item_ids.has(item_id)) {
				duplicates.quantity += quantity;
				duplicates.row_count++;
				if (duplicates.samples.length < SAMPLE_LIMIT) duplicates.samples.push({ class_code, color_code, quantity });
				return;
			}
			seen_item_ids.add(item_id);
		}

		// 금액도 같은 수량 기준이어야 한다. API 의 item_total_value 는 cost × on_hand 라 쓰지 않는다.
		const value = include_value ? quantity * toNumber(row.cost) : 0;
		source_quantity += quantity;
		source_value += value;

		const position = class_index.get(class_code);
		const column = color_index.get(color_code);

		if (!position || column === undefined) {
			unmapped.quantity += quantity;
			unmapped.value += value;
			// 서로 상쇄되는 양수·음수라도 수량이 있는 행은 모두 안내 대상이다.
			if (quantity !== 0) {
				unmapped.row_count++;
				if (unmapped.samples.length < SAMPLE_LIMIT) unmapped.samples.push({ class_code, color_code, quantity });
			}
			return;
		}

		const block = blocks[position.block_index];
		const cell = block.groups[position.group_index].rows[position.row_index].cells[column];
		cell.quantity += quantity;
		cell.value += value;
		block.color_quantities[column] += quantity;
		block.color_values[column] += value;
		block.quantity += quantity;
		block.value += value;

		// raw-data API 는 cost 가 NULL 이어도 IFNULL 로 cost=0 을 내려주므로 수량은 있는데 금액이 0인 행으로 잡는다.
		if (include_value && quantity !== 0 && value === 0) {
			zero_value.rows++;
			zero_value.quantity += quantity;
		}
	});

	const total_quantity = blocks.reduce((sum, block) => sum + block.quantity, 0);
	const total_value = blocks.reduce((sum, block) => sum + block.value, 0);

	return {
		blocks,
		colors: CATCH_COLORS,
		include_value,
		unmapped,
		duplicates,
		excluded,
		zero_value,
		source_quantity,
		source_value,
		mapped_quantity: total_quantity,
		mapped_value: total_value,
		total_quantity,
		total_value,
	};
}

function sampleText(samples, row_count) {
	const text = samples.map(sample => `${sample.class_code}/${sample.color_code}(${sample.quantity})`).join(', ');
	return text + (row_count > samples.length ? ' ...' : '');
}

/** 양식에 담지 못한 대상 재고(20" 클로저·새 색상 등)가 있으면 안내 문구. 없으면 빈 문자열. */
export function catchStockUnmappedMessage(matrix) {
	const unmapped = matrix.unmapped;
	if (!unmapped || unmapped.row_count === 0) return '';
	return `NOT ON THIS SHEET: ${unmapped.row_count} item(s), qty ${unmapped.quantity} - ${sampleText(unmapped.samples, unmapped.row_count)}`;
}

/** 같은 item 이 두 번 이상 내려와 합산에서 뺀 행이 있으면 안내 문구. 없으면 빈 문자열. */
export function catchStockDuplicateMessage(matrix) {
	const duplicates = matrix.duplicates;
	if (!duplicates || duplicates.row_count === 0) return '';
	return `DUPLICATE ROWS SKIPPED: ${duplicates.row_count} row(s), qty ${duplicates.quantity} - ${sampleText(duplicates.samples, duplicates.row_count)}`;
}

/** 금액 0으로 실린 재고가 있으면 TTL PRICE 가 축소되었음을 알린다. 없으면 빈 문자열. */
export function catchStockZeroValueMessage(matrix) {
	const zero_value = matrix.zero_value;
	if (!matrix.include_value || !zero_value || zero_value.rows === 0) return '';
	return `NO COST: ${zero_value.rows} item(s), qty ${zero_value.quantity} counted at $0 - TTL PRICE is lower by that much.`;
}

/** 화면 경고와 NOTE 칸에 쓸 문구 목록. */
export function catchStockMessages(matrix) {
	return [catchStockUnmappedMessage(matrix), catchStockDuplicateMessage(matrix), catchStockZeroValueMessage(matrix)].filter(Boolean);
}

/** NOTE 칸에 넣을 경고 문구. 없으면 빈 문자열. */
export function catchStockNoteText(matrix) {
	return catchStockMessages(matrix).join('  |  ');
}

// ---------------------------------------------------------------------------
// 시트 레이아웃. 고객 FORM 과 같은 A~Z 26열(A 비움, B 부터 시작)·53행을 쓴다.
// 금액을 만들지 않을 때도 금액 행·TTL PRICE 칸을 빈칸으로 남겨 셀 위치를 고정한다.
// ---------------------------------------------------------------------------

const COLUMN_COUNT = 26;

// 고객 FORM 의 열 너비(문자 단위).
const COLUMN_WIDTHS = [9.14, 18.57, 14.14, 14.14, 14.14, 14.14, 14.14, 13.43, 11.57, 29.0, 12.0, 11.57, 11.86, 11.86, 11.57, 30.71, 11.57, 17.71, 11.57, 11.57, 11.57, 11.57, 12.0, 11.57, 11.57, 20.86];

// 열 위치(A=0). 좌 블록 색상 C~G, 우 블록 색상 Q~U.
const LEFT_COLOR_START = 2;
const RIGHT_COLOR_START = 16;
const LABEL_COLUMNS = { left: 1, middle: 9, right: 15, end: 25 };

// 고객 FORM 의 행 높이(pt). xlsx-js-style 은 기본 96DPI 에서 hpx 값을 그대로 pt 로 기록하므로 pt 값을 넣는다.
// 위·아래 절반의 같은 위치 행 높이가 조금씩 달라 절반별로 맞춘다.
const ROW_HEIGHT = {
	title: 48,
	form: 47.25,
	note: 46.5,
	halves: [
		{ header: 35.25, first_bundle: 36, last_bundle: 36.75, spacer: 9.75, subtotal: 32.25 },
		{ header: 34.5, first_bundle: 36.75, last_bundle: 36.75, spacer: 12.75, subtotal: 28.5 },
	],
	data: 36,
};

// 위: BW · JERRY CURL · DEEP, 아래: ST · (빈칸) · BOHEMIAN. 오른쪽 끝 라벨은 좌 블록 이름을 반복한다.
const HALVES = [
	{ left: 'BW', right: 'DEEP', middle_label: 'JERRY CURL', middle_color: 'NAT' },
	{ left: 'ST', right: 'BOHEMIAN', middle_label: '', middle_color: '' },
];

const FILL_WHITE = 'FFFFFF';

const FONT_TITLE = { sz: 40, bold: true };
const FONT_FORM_LABEL = { sz: 20, bold: true };
const FONT_FACTORY_LABEL = { sz: 14, bold: true };
const FONT_DATE_LABEL = { sz: 22 };
const FONT_DATE = { sz: 24, bold: true };
const FONT_BLOCK_LABEL = { sz: 26, bold: true };
const FONT_MIDDLE_LABEL = { sz: 24, bold: true };
const FONT_COLOR_HEADER = { sz: 16, bold: true };
const FONT_LENGTH_LABEL = { sz: 26, bold: true };
const FONT_CLOSURE_LABEL = { sz: 20, bold: true };
const FONT_DATA = { sz: 28 };
const FONT_SUBTOTAL_LABEL = { sz: 16, bold: true };
const FONT_SUBTOTAL = { sz: 16 };
const FONT_NOTE_LABEL = { sz: 26, bold: true };
const FONT_NOTE_TEXT = { sz: 14 };
const FONT_TOTAL = { sz: 28, bold: true };

const DATE_FORMAT = 'mm/dd/yy;@';
const TOTAL_QUANTITY_FORMAT = '0';
const VALUE_FORMAT = '"$"#,##0.00';

const THIN = { style: 'thin' };
const MEDIUM = { style: 'medium' };
const DOUBLE = { style: 'double' };

function border(top, right, bottom, left) {
	const result = {};
	if (top) result.top = top;
	if (right) result.right = right;
	if (bottom) result.bottom = bottom;
	if (left) result.left = left;
	return result;
}

const CENTER = { horizontal: 'center', vertical: 'center' };

function style(font, cell_border, extra) {
	const result = { font, alignment: CENTER, border: cell_border, fill: { fgColor: { rgb: FILL_WHITE } } };
	if (extra) Object.assign(result, extra);
	return result;
}

function textCell(value, cell_style, span) {
	const cell = { t: 's', v: value };
	if (cell_style) cell.s = cell_style;
	if (span) Object.assign(cell, span);
	return cell;
}

function numberCell(value, cell_style, span) {
	const cell = { t: 'n', v: value, s: cell_style };
	if (span) Object.assign(cell, span);
	return cell;
}

/** 표 영역(3~52행)의 기본 테두리. 바깥 B 열 왼쪽과 Z 열 오른쪽만 굵게 둘러싼다. */
function gridBorder(column, top, bottom) {
	return border(top || THIN, column === LABEL_COLUMNS.end ? MEDIUM : THIN, bottom || THIN, column === LABEL_COLUMNS.left ? MEDIUM : THIN);
}

function leadingCell(height) {
	return textCell('', null, { height: { hpx: height } });
}

function isLeftColor(column) {
	return column >= LEFT_COLOR_START && column < LEFT_COLOR_START + CATCH_COLORS.length;
}

function isRightColor(column) {
	return column >= RIGHT_COLOR_START && column < RIGHT_COLOR_START + CATCH_COLORS.length;
}

function blockOf(matrix, key) {
	return matrix.blocks.find(block => block.key === key);
}

/** 블록 헤더 행: 좌·중·우·끝 라벨과 색상 라벨. 열 너비는 배열 인덱스 = 열 위치가 성립하는 이 행에서만 지정한다. */
function headerRow(half, height, set_widths) {
	const label_style = column => style(FONT_BLOCK_LABEL, gridBorder(column, null, MEDIUM));
	const color_style = column => style(FONT_COLOR_HEADER, gridBorder(column, null, MEDIUM));
	const row = [leadingCell(height)];
	for (let column = 1; column < COLUMN_COUNT; column++) {
		let cell;
		if (column === LABEL_COLUMNS.left || column === LABEL_COLUMNS.end) cell = textCell(half.left, label_style(column));
		else if (column === LABEL_COLUMNS.right) cell = textCell(half.right, label_style(column));
		else if (column === LABEL_COLUMNS.middle) cell = textCell(half.middle_label, style(FONT_MIDDLE_LABEL, gridBorder(column, null, MEDIUM)));
		else if (column === LABEL_COLUMNS.middle + 1) cell = textCell(half.middle_color, color_style(column));
		else if (isLeftColor(column)) cell = textCell(CATCH_COLORS[column - LEFT_COLOR_START].label, color_style(column));
		else if (isRightColor(column)) cell = textCell(CATCH_COLORS[column - RIGHT_COLOR_START].label, color_style(column));
		else cell = textCell('', color_style(column));
		row.push(cell);
	}
	if (set_widths) row.forEach((cell, index) => (cell.width = { wch: COLUMN_WIDTHS[index] }));
	return row;
}

/**
 * 데이터 행. left_cells / right_cells 가 null 이면 그 블록 색상 칸을 비운다(13X5 처럼 수집하지 않는 행).
 * show_middle_label 이 true 면 JERRY CURL 라벨 열(J)에도 행 라벨을 쓴다.
 */
function dataRow(label, label_font, left_cells, right_cells, show_middle_label, height) {
	const data_style = column => style(FONT_DATA, gridBorder(column));
	const label_style = column => style(label_font, gridBorder(column));
	const row = [leadingCell(height)];
	for (let column = 1; column < COLUMN_COUNT; column++) {
		let cell;
		if (column === LABEL_COLUMNS.left || column === LABEL_COLUMNS.right || column === LABEL_COLUMNS.end) cell = typeof label === 'number' ? numberCell(label, label_style(column)) : textCell(label, label_style(column));
		else if (column === LABEL_COLUMNS.middle) cell = show_middle_label ? (typeof label === 'number' ? numberCell(label, label_style(column)) : textCell(label, label_style(column))) : textCell('', data_style(column));
		else if (isLeftColor(column) && left_cells) cell = numberCell(left_cells[column - LEFT_COLOR_START].quantity, data_style(column));
		else if (isRightColor(column) && right_cells) cell = numberCell(right_cells[column - RIGHT_COLOR_START].quantity, data_style(column));
		else cell = textCell('', data_style(column));
		row.push(cell);
	}
	return row;
}

function spacerRow(height) {
	const row = [leadingCell(height)];
	for (let column = 1; column < COLUMN_COUNT; column++) row.push(textCell('', style(FONT_DATA, border(THIN, column === LABEL_COLUMNS.end ? MEDIUM : THIN, null, column === LABEL_COLUMNS.left ? MEDIUM : THIN))));
	return row;
}

/**
 * 소계 두 행. 1행은 색상별 수량, 2행은 색상별 금액(금액을 만들지 않으면 빈칸).
 * STTL 라벨(B)과 절반 총수량(Z)은 두 행에 걸친다. 수집하지 않는 열은 빈칸으로 둔다.
 */
function subtotalRows(left, right, half_quantity, include_value, height) {
	const quantity_style = column => style(FONT_SUBTOTAL, gridBorder(column, MEDIUM, THIN));
	const value_style = column => style(FONT_SUBTOTAL, gridBorder(column, THIN, MEDIUM));

	const quantity_row = [leadingCell(height)];
	quantity_row.push(textCell('STTL', style(FONT_SUBTOTAL_LABEL, border(MEDIUM, MEDIUM, MEDIUM, MEDIUM)), { rs: 2 }));
	for (let column = 2; column < LABEL_COLUMNS.end; column++) {
		if (isLeftColor(column)) quantity_row.push(numberCell(left.color_quantities[column - LEFT_COLOR_START], quantity_style(column)));
		else if (isRightColor(column)) quantity_row.push(numberCell(right.color_quantities[column - RIGHT_COLOR_START], quantity_style(column)));
		else quantity_row.push(textCell('', quantity_style(column)));
	}
	quantity_row.push(numberCell(half_quantity, style(FONT_SUBTOTAL_LABEL, border(MEDIUM, MEDIUM, MEDIUM, MEDIUM)), { rs: 2 }));

	// B 열(STTL)과 Z 열(절반 총수량)은 위 행의 rowspan 이 차지하므로 셀을 넣지 않는다.
	const value_row = [leadingCell(height)];
	for (let column = 2; column < LABEL_COLUMNS.end; column++) {
		if (include_value && isLeftColor(column)) value_row.push(numberCell(round2(left.color_values[column - LEFT_COLOR_START]), value_style(column)));
		else if (include_value && isRightColor(column)) value_row.push(numberCell(round2(right.color_values[column - RIGHT_COLOR_START]), value_style(column)));
		else value_row.push(textCell('', value_style(column)));
	}
	return [quantity_row, value_row];
}

/**
 * excelComplicated() 가 그대로 받는 2차원 셀 배열을 만든다.
 * 배열 인덱스 = 열 위치(A=0 ... Z=25). 단, 위 행의 rowspan 이 차지한 열에는 셀을 넣지 않는다.
 */
export function buildCatchStockSheetCells(matrix, options) {
	const report_date = (options && options.report_date) || new Date();
	const include_value = matrix.include_value;
	const data = [];

	// 1행: 제목
	data.push([leadingCell(ROW_HEIGHT.title), textCell(CATCH_SHEET_TITLE, style(FONT_TITLE, border(MEDIUM, MEDIUM, MEDIUM, MEDIUM)), { cs: COLUMN_COUNT - 1 })]);

	// 2행: ACCOUNT / FACTORY NAME / ETA 는 고객이 손으로 채우는 빈칸이고 DATE 만 다운로드 날짜로 채운다.
	const form_border = (left, right) => border(MEDIUM, right || THIN, DOUBLE, left || THIN);
	data.push([
		leadingCell(ROW_HEIGHT.form),
		textCell('ACCOUNT', style(FONT_FORM_LABEL, form_border(MEDIUM)), { cs: 2 }),
		textCell('', style(FONT_FORM_LABEL, form_border()), { cs: 5 }),
		textCell('FACTORY NAME', style(FONT_FACTORY_LABEL, form_border()), { cs: 2 }),
		textCell('', style(FONT_FORM_LABEL, form_border()), { cs: 6 }),
		textCell('ETA', style(FONT_FORM_LABEL, form_border()), { cs: 2 }),
		textCell('', style(FONT_FORM_LABEL, form_border()), { cs: 3 }),
		textCell('DATE', style(FONT_DATE_LABEL, form_border()), { cs: 2 }),
		numberCell(excelDateSerial(report_date), style(FONT_DATE, form_border(null, MEDIUM), { numFmt: DATE_FORMAT }), { cs: 3 }),
	]);

	HALVES.forEach((half, half_index) => {
		const heights = ROW_HEIGHT.halves[half_index];
		const left = blockOf(matrix, half.left);
		const right = blockOf(matrix, half.right);
		const show_middle_label = !!half.middle_label;

		data.push(headerRow(half, heights.header, half_index === 0));

		// 번들 10~30"
		const left_bundle = left.groups.find(group => group.key === 'BUNDLE').rows;
		const right_bundle = right.groups.find(group => group.key === 'BUNDLE').rows;
		left_bundle.forEach((bundle_row, index) => {
			const height = index === 0 ? heights.first_bundle : index === left_bundle.length - 1 ? heights.last_bundle : ROW_HEIGHT.data;
			data.push(dataRow(bundle_row.length, FONT_LENGTH_LABEL, bundle_row.cells, right_bundle[index].cells, show_middle_label, height));
		});

		data.push(spacerRow(heights.spacer));

		// 5X5 클로저 10~18"
		const left_closure = left.groups.find(group => group.key === '5X5').rows;
		const right_closure = right.groups.find(group => group.key === '5X5').rows;
		left_closure.forEach((closure_row, index) => {
			data.push(dataRow('5X5 ' + closure_row.length, FONT_CLOSURE_LABEL, closure_row.cells, right_closure[index].cells, show_middle_label, ROW_HEIGHT.data));
		});

		// 13X5 10~18": 요청 범위 밖이라 라벨만 남기고 수량 칸은 비운다.
		evenLengths(10, 18).forEach(length => data.push(dataRow('13X5 ' + length, FONT_CLOSURE_LABEL, null, null, show_middle_label, ROW_HEIGHT.data)));

		subtotalRows(left, right, left.quantity + right.quantity, include_value, heights.subtotal).forEach(row => data.push(row));
	});

	// 53행: NOTE / TTL AMT(총수량) / TTL PRICE
	const note_border = border(MEDIUM, MEDIUM, MEDIUM, MEDIUM);
	data.push([
		leadingCell(ROW_HEIGHT.note),
		textCell('NOTE', style(FONT_NOTE_LABEL, note_border)),
		textCell(catchStockNoteText(matrix), style(FONT_NOTE_TEXT, note_border, { alignment: { horizontal: 'left', vertical: 'center', wrapText: true } }), { cs: 13 }),
		textCell('TTL AMT', style(FONT_TOTAL, note_border), { cs: 2 }),
		numberCell(matrix.total_quantity, style(FONT_TOTAL, note_border, { numFmt: TOTAL_QUANTITY_FORMAT }), { cs: 2 }),
		textCell(include_value ? 'TTL  PRICE' : '', style(FONT_TOTAL, note_border), { cs: 3 }),
		include_value ? numberCell(round2(matrix.total_value), style(FONT_TOTAL, note_border, { numFmt: VALUE_FORMAT }), { cs: 4 }) : textCell('', style(FONT_TOTAL, note_border), { cs: 4 }),
	]);

	return data;
}

/** catchStockSheet_YYYYMMDD.xlsx */
export function catchStockFileName(report_date) {
	const date = report_date || new Date();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `catchStockSheet_${date.getFullYear()}${month}${day}.xlsx`;
}
